import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, Upload, Search, Globe, Check, GitMerge, Ban, RefreshCw, ChevronDown, ShoppingCart, Loader2, CheckCircle2 } from 'lucide-react';
import { IngredientMergeModal } from './IngredientMergeModal';
import { IngredientRejectModal } from './IngredientRejectModal';
import { useStandardIngredientsStore } from '../../store/useStandardIngredientsStore';
import { StandardIngredient, LanguageCode, IngredientStatus } from '../../types/ingredients';
import { downloadCSV, parseCSV } from '../../utils/csv';
import {
  rebuildIngredientCatalogApi,
  type RebuildLanguageOption,
} from '../../utils/ingredientsModerationApi';
import { scrapeIngredient } from '../../utils/scrapingApi';
import { RetailerProduct } from '../../types/retailerProduct';
import { ScrapingResultModal } from './ScrapingResultModal';
import {
  getUnitDimension,
  hasCiqualNutrition,
  hasConversionComplete,
  needsUnitConversion,
} from '../../utils/unitDimension';

type StatusFilter = 'all' | 'pending' | 'validated';
type DataFilter =
  | 'all'
  | 'ciqual_ok'
  | 'ciqual_missing'
  | 'conversion_ok'
  | 'conversion_missing'
  | 'volume'
  | 'piece';

function getEffectiveStatus(ingredient: StandardIngredient): IngredientStatus {
  return ingredient.status === 'pending' ? 'pending' : 'validated';
}

const LANGUAGES: { code: LanguageCode; label: string; flag: string }[] = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' }
];

// Update category translations to use category codes
const CATEGORY_LABELS: Record<string, Record<string, string>> = {
  en: {
    vegetable: 'Vegetable',
    fruit: 'Fruit',
    meat: 'Meat',
    fish: 'Fish',
    dairy: 'Dairy',
    grain: 'Grain',
    spice: 'Spice',
    other: 'Other',
    beverages: 'Beverages',
    sweetGrocery: 'Sweet Grocery',
    savoryGrocery: 'Savory Grocery',
    condiments: 'Condiments and Spices',
    produce: 'Produce',
  },
  fr: {
    vegetable: 'Légume',
    fruit: 'Fruit',
    meat: 'Viande',
    fish: 'Poisson',
    dairy: 'Produit laitier',
    grain: 'Céréale',
    spice: 'Épice',
    other: 'Autre',
    beverages: 'Boissons',
    sweetGrocery: 'Épicerie Sucrée',
    savoryGrocery: 'Épicerie Salée',
    condiments: 'Condiments et Épices',
    produce: 'Fruits et Légumes',
  }
};

function getCategoryLabel(category: string, lang: string) {
  return CATEGORY_LABELS[lang]?.[category] || category;
}

export function StandardIngredients() {
  const navigate = useNavigate();
  const [mergingIngredient, setMergingIngredient] = useState<StandardIngredient | null>(null);
  const [rejectingIngredient, setRejectingIngredient] = useState<StandardIngredient | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dataFilter, setDataFilter] = useState<DataFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [rebuildMenuOpen, setRebuildMenuOpen] = useState(false);
  const [rebuilding, setRebuilding] = useState<RebuildLanguageOption | null>(null);
  const [scrapingIngredientId, setScrapingIngredientId] = useState<string | null>(null);
  const [scrapeModalData, setScrapeModalData] = useState<{
    ingredientName: string;
    products: RetailerProduct[];
  } | null>(null);
  const {
    ingredients,
    addIngredient,
    removeIngredient,
    searchTerm,
    selectedLanguages,
    setSearchTerm,
    toggleLanguage,
    fetchIngredients,
    isLoading,
  } = useStandardIngredientsStore();

  // Fetch once on mount (and when language filter changes). No persistent
  // listener — avoids re-reads on every network reconnection or token refresh.
  useEffect(() => {
    fetchIngredients();
  }, [selectedLanguages.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingCount = useMemo(
    () => ingredients.filter((i) => getEffectiveStatus(i) === 'pending').length,
    [ingredients]
  );

  const handleExport = () => {
    const exportData = ingredients.map(ingredient => ({
      name: ingredient.name,
      language: ingredient.language,
      category: ingredient.category,
      aliases: (ingredient.aliases || []).join(';'),
      preferredUnit: ingredient.preferredUnit,
      picture: ingredient.picture || '',
      attributes: {
        glutenFree: ingredient.attributes?.glutenFree || false,
        vegan: ingredient.attributes?.vegan || false,
        vegetarian: ingredient.attributes?.vegetarian || false,
        calories: ingredient.attributes?.calories && !Number.isNaN(ingredient.attributes.calories) ? ingredient.attributes.calories : 0,
        organic: ingredient.attributes?.organic || false,
        allergens: (ingredient.attributes?.allergens || []).join(';'),
        seasonal: (ingredient.attributes?.seasonal || []).join(';')
      }
    }));
    downloadCSV(exportData, 'standard-ingredients.csv');
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const data = await parseCSV(file);
      for (const item of data) {
        await addIngredient(item);
      }
    } catch (error) {
      console.error('Error importing ingredients:', error);
      setError('Failed to import ingredients');
    }
  };

  const handleDeleteIngredient = async (id: string) => {
    try {
      await removeIngredient(id);
    } catch (error) {
      console.error('Error removing ingredient:', error);
      setError('Failed to remove ingredient');
    }
  };

  const handleScrapeIngredient = async (ingredient: StandardIngredient) => {
    setScrapingIngredientId(ingredient.id);
    setError(null);
    try {
      const result = await scrapeIngredient(ingredient.id, 'carrefour');
      setScrapeModalData({ ingredientName: ingredient.name, products: result.products });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur scraping';
      setError(`Scraping "${ingredient.name}" : ${message}`);
    } finally {
      setScrapingIngredientId(null);
    }
  };


  const filteredIngredients = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    const filtered = ingredients.filter((ingredient) => {
      const languageMatch =
        selectedLanguages.length === 0 || selectedLanguages.includes(ingredient.language);
      const searchMatch =
        search === '' ||
        ingredient.name.toLowerCase().includes(search) ||
        (ingredient.aliases || []).some((alias) =>
          String(alias || '')
            .toLowerCase()
            .includes(search)
        );

      const effectiveStatus = getEffectiveStatus(ingredient);
      const statusMatch =
        statusFilter === 'all' ||
        (statusFilter === 'pending' && effectiveStatus === 'pending') ||
        (statusFilter === 'validated' && effectiveStatus === 'validated');

      const dim = getUnitDimension(ingredient.preferredUnit);
      const dataMatch =
        dataFilter === 'all' ||
        (dataFilter === 'ciqual_ok' && hasCiqualNutrition(ingredient)) ||
        (dataFilter === 'ciqual_missing' && !hasCiqualNutrition(ingredient)) ||
        (dataFilter === 'conversion_ok' && hasConversionComplete(ingredient)) ||
        (dataFilter === 'conversion_missing' &&
          needsUnitConversion(ingredient) &&
          !hasConversionComplete(ingredient)) ||
        (dataFilter === 'volume' && dim === 'volume') ||
        (dataFilter === 'piece' && dim === 'count');

      return languageMatch && searchMatch && statusMatch && dataMatch;
    });

    // Tri par défaut : pending en haut quand on est sur "Tous"
    if (statusFilter === 'all') {
      return [...filtered].sort((a, b) => {
        const aPending = getEffectiveStatus(a) === 'pending' ? 0 : 1;
        const bPending = getEffectiveStatus(b) === 'pending' ? 0 : 1;
        if (aPending !== bPending) return aPending - bPending;
        return a.name.localeCompare(b.name);
      });
    }
    return filtered;
  }, [ingredients, selectedLanguages, searchTerm, statusFilter, dataFilter]);

  const handleMergeSuccess = (result: { mergedInto: string; recipesUpdated: number }) => {
    setMergingIngredient(null);
    setInfo(`Fusion réussie. ${result.recipesUpdated} recette(s) mise(s) à jour.`);
  };

  const handleRejectSuccess = (result: { recipesUpdated: number }) => {
    setRejectingIngredient(null);
    setInfo(`Ingrédient rejeté. ${result.recipesUpdated} recette(s) ont été nettoyée(s).`);
  };

  const handleRebuildCatalog = async (option: RebuildLanguageOption) => {
    setRebuildMenuOpen(false);
    setError(null);
    setInfo(null);
    setRebuilding(option);
    try {
      const result = await rebuildIngredientCatalogApi(option);
      const parts: string[] = [];
      for (const [lang, summary] of Object.entries(result.summary)) {
        if (summary.ok) {
          parts.push(`${lang.toUpperCase()} : ${summary.size} entrées (${summary.kb} Ko, v${summary.version})`);
        } else {
          parts.push(`${lang.toUpperCase()} : refusé (${summary.reason})`);
        }
      }
      const refusedCount = result.refused?.length || 0;
      if (refusedCount > 0) {
        setError(
          `Reconstruction partielle (${result.scanned} scannés, ${result.skipped} ignorés). ${parts.join(' • ')}`
        );
      } else {
        setInfo(
          `Catalogue reconstruit (${result.scanned} scannés, ${result.skipped} ignorés). ${parts.join(' • ')}`
        );
      }
    } catch (err) {
      console.error('Rebuild catalog failed:', err);
      const message = err instanceof Error ? err.message : 'Échec de la reconstruction du catalogue';
      setError(message);
    } finally {
      setRebuilding(null);
    }
  };

  const filterPills: { value: StatusFilter; label: string; count: number | null }[] = [
    { value: 'all', label: 'Tous', count: null },
    { value: 'pending', label: 'En attente', count: pendingCount },
    { value: 'validated', label: 'Validés', count: null }
  ];

  const dataFilterPills: { value: DataFilter; label: string }[] = [
    { value: 'all', label: 'Données : tous' },
    { value: 'ciqual_ok', label: 'CIQUAL OK' },
    { value: 'ciqual_missing', label: 'CIQUAL manquant' },
    { value: 'conversion_ok', label: 'Conversion OK' },
    { value: 'conversion_missing', label: 'Conversion à compléter' },
    { value: 'volume', label: 'Liquides / volume' },
    { value: 'piece', label: 'Pièces' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-display font-bold text-menioo-navy">
            Standard Ingredients
          </h2>
          {pendingCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
              {pendingCount} en attente
            </span>
          )}
        </div>
        <div className="flex space-x-4">
          <div className="relative">
            <button
              type="button"
              onClick={() => setRebuildMenuOpen((open) => !open)}
              disabled={rebuilding !== null}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-menioo-green disabled:opacity-60 disabled:cursor-not-allowed"
              title="Reconstruit le document agrégé `ingredientCatalogs/{lang}` depuis la collection `standardIngredients` (validated uniquement)."
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${rebuilding ? 'animate-spin' : ''}`} />
              {rebuilding ? `Reconstruction…` : 'Rebuild catalog'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
            {rebuildMenuOpen && rebuilding === null && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  <button
                    type="button"
                    onClick={() => handleRebuildCatalog('fr')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    🇫🇷 Français uniquement
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRebuildCatalog('en')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    🇬🇧 English only
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRebuildCatalog('all')}
                    className="block w-full text-left px-4 py-2 text-sm font-medium text-menioo-navy border-t border-gray-100 hover:bg-gray-50"
                  >
                    🌍 Toutes les langues
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => fetchIngredients()}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-menioo-green disabled:opacity-60 disabled:cursor-not-allowed"
            title="Recharger la liste depuis Firestore"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-menioo-green bg-menioo-green-light hover:bg-menioo-green-lighter focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-menioo-green"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
          <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-menioo-green bg-menioo-green-light hover:bg-menioo-green-lighter focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-menioo-green cursor-pointer">
            <Upload className="h-4 w-4 mr-2" />
            Import
            <input
              type="file"
              accept=".csv"
              onChange={handleImport}
              className="hidden"
            />
          </label>
          <button
            onClick={() => navigate('/admin/ingredients/new')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-menioo-green hover:bg-menioo-green-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-menioo-green"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Ingredient
          </button>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap items-center gap-2">
        {filterPills.map((pill) => {
          const active = statusFilter === pill.value;
          return (
            <button
              key={pill.value}
              type="button"
              onClick={() => setStatusFilter(pill.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors flex items-center gap-1.5 ${
                active
                  ? 'bg-menioo-navy text-white border-menioo-navy'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {pill.label}
              {pill.count != null && pill.count > 0 && (
                <span
                  className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-bold ${
                    active ? 'bg-white text-menioo-navy' : 'bg-orange-100 text-orange-800'
                  }`}
                >
                  {pill.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {dataFilterPills.map((pill) => {
          const active = dataFilter === pill.value;
          return (
            <button
              key={pill.value}
              type="button"
              onClick={() => setDataFilter(pill.value)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? 'bg-menioo-green text-white border-menioo-green'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>

      {/* Language Filter and Search */}
      <div className="mb-6 flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <Globe className="h-5 w-5 text-gray-400" />
          {LANGUAGES.map(({ code, label, flag }) => (
            <label
              key={code}
              className="inline-flex items-center space-x-2 text-sm"
            >
              <input
                type="checkbox"
                checked={selectedLanguages.includes(code)}
                onChange={() => toggleLanguage(code)}
                className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
              />
              <span>
                {flag} {label}
              </span>
            </label>
          ))}
        </div>
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="search"
            placeholder="Search ingredients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-menioo-green focus:border-menioo-green sm:text-sm"
          />
        </div>
      </div>

      {info && (
        <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800 flex items-start justify-between">
          <span>{info}</span>
          <button
            type="button"
            onClick={() => setInfo(null)}
            className="text-green-700 hover:text-green-900 ml-3 font-medium text-xs"
          >
            Fermer
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-start justify-between">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-red-700 hover:text-red-900 ml-3 font-medium text-xs"
          >
            Fermer
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full table-fixed divide-y divide-gray-200">
          <colgroup>
            <col className="w-[16%]" />
            <col className="w-[10%]" />
            <col className="w-[6%]" />
            <col className="w-[6%]" />
            <col className="w-[14%]" />
            <col className="w-[10%]" />
            <col className="w-[6%]" />
            <col className="w-[32%]" />
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" title="Match CIQUAL">
                CIQUAL
              </th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" title="Conversion pièce/volume">
                Conv.
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lang</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredIngredients.map((ingredient) => {
              const isPending = getEffectiveStatus(ingredient) === 'pending';
              const ciqualOk = hasCiqualNutrition(ingredient);
              const conversionOk = hasConversionComplete(ingredient);
              return (
                <tr key={ingredient.id} className={isPending ? 'bg-orange-50/40' : ''}>
                  <td className="px-3 py-2 max-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate" title={ingredient.name}>
                      {ingredient.name}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {isPending ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        En attente
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Validé
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-center">
                    {ciqualOk ? (
                      <span
                        className="inline-flex"
                        title={
                          ingredient.nutrition?.matchedName
                            ? `CIQUAL : ${ingredient.nutrition.matchedName}`
                            : 'CIQUAL renseigné'
                        }
                      >
                        <CheckCircle2 className="h-5 w-5 text-menioo-green" />
                      </span>
                    ) : (
                      <span className="text-gray-300" title="Pas de match CIQUAL">
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-center">
                    {conversionOk ? (
                      <span
                        className="inline-flex"
                        title={
                          ingredient.unitWeight_g != null
                            ? `Pièce : ${ingredient.unitWeight_g} g`
                            : ingredient.density_g_per_mL != null
                              ? `Densité : ${ingredient.density_g_per_mL.toFixed(3)} g/mL`
                              : 'Conversion OK'
                        }
                      >
                        <CheckCircle2 className="h-5 w-5 text-menioo-green" />
                      </span>
                    ) : (
                      <span
                        className="text-gray-300"
                        title="Conversion à compléter"
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-0">
                    <div
                      className="text-sm text-gray-900 truncate"
                      title={getCategoryLabel(ingredient.category, ingredient.language === 'fr' ? 'fr' : 'en')}
                    >
                      {getCategoryLabel(ingredient.category, ingredient.language === 'fr' ? 'fr' : 'en')}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                    {ingredient.preferredUnit}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <span title={LANGUAGES.find((l) => l.code === ingredient.language)?.label}>
                      {LANGUAGES.find((l) => l.code === ingredient.language)?.flag || ingredient.language}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                    {isPending ? (
                      <div className="inline-flex justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/ingredients/${ingredient.id}/edit`)}
                          title="Valider (corriger puis enregistrer)"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-menioo-green rounded hover:bg-menioo-green-dark"
                        >
                          <Check className="h-3.5 w-3.5" />
                          Valider
                        </button>
                        <button
                          type="button"
                          onClick={() => setMergingIngredient(ingredient)}
                          title="Fusionner avec un ingrédient existant"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
                        >
                          <GitMerge className="h-3.5 w-3.5" />
                          Merger
                        </button>
                        <button
                          type="button"
                          onClick={() => setRejectingIngredient(ingredient)}
                          title="Rejeter et retirer des recettes"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                        >
                          <Ban className="h-3.5 w-3.5" />
                          Rejeter
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/ingredients/${ingredient.id}/edit`)}
                          className="text-menioo-green hover:text-menioo-green-dark"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleScrapeIngredient(ingredient)}
                          disabled={scrapingIngredientId === ingredient.id}
                          className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Scraper Carrefour"
                        >
                          {scrapingIngredientId === ingredient.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <ShoppingCart className="h-3.5 w-3.5" />
                          )}
                          Scraper
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteIngredient(ingredient.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {mergingIngredient && (
        <IngredientMergeModal
          source={mergingIngredient}
          catalog={ingredients}
          onClose={() => setMergingIngredient(null)}
          onSuccess={handleMergeSuccess}
        />
      )}

      {rejectingIngredient && (
        <IngredientRejectModal
          ingredient={rejectingIngredient}
          onClose={() => setRejectingIngredient(null)}
          onSuccess={handleRejectSuccess}
        />
      )}

      {scrapeModalData && (
        <ScrapingResultModal
          ingredientName={scrapeModalData.ingredientName}
          products={scrapeModalData.products}
          onClose={() => setScrapeModalData(null)}
        />
      )}
    </div>
  );
}